import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';

import { Icon, Text } from 'shared/components/ui';
import { CompaniesFilters, Header, PeopleFilters, Sidebar } from 'features/technographics';
import { SearchFormParams } from 'features/search/models';
import { CompanySearchFormParams } from 'features/technographics/models';
import { useFilters } from 'features/search';
import { useTechnographicsSearch } from 'features/technographics';
import { CompanySearchParams, SearchParams } from 'shared/generated-models';

import styles from './index.module.scss';

export const TechnographicsFilters = () => {
	const filters = useFilters() as SearchParams & CompanySearchParams;
	const navigate = useNavigate();

	const [tags, setTags] = useState<string[]>(filters['tags']?.split(',') || []);

	const search = useTechnographicsSearch();

	const handleShowResults = () => {
		search({
			...formikPeople.values,
			...formikCompanies.values,
			required_skills: tags!,
			skills: tags!,
			page: 0,
			technographics_page: 0
		});
		return;
	};

	const peopleInitialValues = useMemo<SearchFormParams>(
		() => ({
			locations: filters['locations'] || [],
			countries: filters['countries'] || [],
			seniority: filters['seniority'] || []
		}),
		[filters['locations'], filters['countries'], filters['seniority']]
	);

	const formikPeople = useFormik<SearchFormParams>({
		initialValues: peopleInitialValues,
		validateOnMount: true,
		onSubmit: values => {
			return;
		}
	});

	const companiesInitialValues = useMemo<CompanySearchFormParams>(
		() => ({
			location: filters['city'] || [],
			country: filters['country'] || [],
			company_domain: filters['company_domain'] || [],
			website_keywords: filters['website_keywords'] || [],
			headcount: filters['headcount'] || []
		}),
		[
			filters['city'],
			filters['country'],
			filters['company_domain'],
			filters['website_keywords'],
			filters['headcount']
		]
	);

	const formikCompanies = useFormik<CompanySearchFormParams>({
		initialValues: companiesInitialValues,
		validateOnMount: true,
		onSubmit: values => {
			return;
		}
	});

	const peopleFilters = useMemo(() => {
		const tags: ['locations' | 'countries' | 'seniority', string][] = [];
		if (
			'locations' in formikPeople.values &&
			!!formikPeople.values['locations'] &&
			formikPeople.values['locations'].length > 0
		) {
			formikPeople.values['locations'].forEach(tag => {
				tags.push(['locations', tag]);
			});
		}
		if (
			'countries' in formikPeople.values &&
			!!formikPeople.values['countries'] &&
			formikPeople.values['countries'].length > 0
		) {
			formikPeople.values['countries'].forEach(tag => {
				tags.push(['countries', tag]);
			});
		}
		if (
			'seniority' in formikPeople.values &&
			!!formikPeople.values['seniority'] &&
			formikPeople.values['seniority'].length > 0
		) {
			formikPeople.values['seniority'].forEach(tag => {
				tags.push(['seniority', tag]);
			});
		}
		return tags;
	}, [formikPeople]);

	const companiesFilters = useMemo(() => {
		const tags: ['city' | 'country' | 'company_domain' | 'website_keywords' | 'headcount', string][] = [];
		if (
			'city' in formikCompanies.values &&
			!!formikCompanies.values['city'] &&
			formikCompanies.values['city'].length > 0
		) {
			formikCompanies.values['city'].forEach(tag => {
				tags.push(['city', tag]);
			});
		}
		if (
			'country' in formikCompanies.values &&
			!!formikCompanies.values['country'] &&
			formikCompanies.values['country'].length > 0
		) {
			formikCompanies.values['country'].forEach(tag => {
				tags.push(['country', tag]);
			});
		}
		if (
			'company_domain' in formikCompanies.values &&
			!!formikCompanies.values['company_domain'] &&
			formikCompanies.values['company_domain'].length > 0
		) {
			formikCompanies.values['company_domain'].forEach(tag => {
				tags.push(['company_domain', tag]);
			});
		}
		if (
			'website_keywords' in formikCompanies.values &&
			!!formikCompanies.values['website_keywords'] &&
			formikCompanies.values['website_keywords'].length > 0
		) {
			formikCompanies.values['website_keywords'].forEach(tag => {
				tags.push(['website_keywords', tag]);
			});
		}
		if (
			'headcount' in formikCompanies.values &&
			!!formikCompanies.values['headcount'] &&
			formikCompanies.values['headcount'].length > 0
		) {
			formikCompanies.values['headcount'].forEach(tag => {
				tags.push(['headcount', tag]);
			});
		}
		return tags;
	}, [formikCompanies]);

	const handleBack = () => {
		navigate('/technographics/chat');
	};

	return (
		<>
			<Header progress="filters" onClick={handleBack} />
			<div className={styles.container}>
				<div className={styles.wrapperContainer}>
					<div className={styles.title}>
						<Text variant="inter/28/semi">Company and People Filters</Text>
						<Text variant="inter/16/regular" color="grey">
							Narrow down your search by adding specific filters for location and seniority
						</Text>
					</div>
					<div className={styles.wrapper}>
						<div className={styles.column}>
							<div className={styles.filtersWrapper}>
								<div className={styles.filtersTitle}>
									<Icon icon="buildings" size={24} className={styles.iconBuildings} />
									<Text variant="inter/18/semi">Companies</Text>
								</div>
								<div className={styles.filtersContainer}>
									<CompaniesFilters formik={formikCompanies} />
								</div>
							</div>
							<div className={styles.filtersWrapper}>
								<div className={styles.filtersTitle}>
									<Icon icon="users" size={24} className={styles.iconPeople} />
									<Text variant="inter/18/semi">People</Text>
								</div>
								<div className={styles.filtersContainer}>
									<PeopleFilters formik={formikPeople} />
								</div>
							</div>
						</div>
						<div className={styles.sidebarWrapper}>
							<Sidebar
								tags={tags!}
								setTags={setTags}
								filters
								peopleFilters={peopleFilters}
								setPeopleFilters={formikPeople.setValues}
								companiesFilters={companiesFilters}
								setCompaniesFilters={formikCompanies.setValues}
								handleNext={handleShowResults}
								textNext="Show results"
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
